/**
 * The run ended, and the socket closed to say so.
 *
 * ## Why this is not in `./join-refusal`
 *
 * That file answers *why a room would not seat this client* and holds three
 * codes that are all refusals. This is the opposite kind of event and the
 * distinction is the whole point: **a run that ended is the game working.** The
 * player descended, played, died, and the design's death penalty ends the run
 * (`docs/GAME-DESIGN.md`). Filing it beside three refusals would invite the next
 * reader to hand it to `describeJoinFailure`, which is precisely the bug this
 * module exists to prevent — a healthy server that answered exactly right,
 * reported to a player as *"the game server could not be reached"*, in the one
 * moment they are least able to tell the difference.
 *
 * It is also a *later* event than any refusal. A refused join happens instead of
 * a run; this happens at the end of one, to a client that has been playing for
 * ten minutes, and it arrives on a different channel — `room.onLeave`, not a
 * rejected `joinRoom` promise.
 *
 * ## The number is restated, exactly as the refusal codes are
 *
 * `apps/web` does not depend on `apps/game` — nothing in this package may, and
 * the deployment has them in separate containers — so 4200 is written here and
 * `descent.room.ts` holds the other copy. Same real cost, same mitigation:
 * a browser test against a live room is what would catch the two drifting
 * apart.
 *
 * The room's own note on the choice, worth keeping on this side too: close
 * codes 4000–4999 are the application's, Colyseus has taken the first four
 * (4000 consented, 4001 shutting down, 4002 with-error, 4003 failed to
 * reconnect), and **4200 must not be 4000** or a run that ended would be
 * indistinguishable from the player pressing the button that leaves.
 *
 * ## The SDK delivers it, and does not reconnect on it
 *
 * Checked against `@colyseus/sdk` 0.17 rather than assumed. `Room.connect`
 * routes a close event to `onDrop` and the automatic reconnection *only* for
 * `NO_STATUS_RECEIVED` (1005), `ABNORMAL_CLOSURE` (1006), `GOING_AWAY` (1001)
 * and `MAY_TRY_RECONNECT` (4010); every other code — 4200 among them — goes
 * straight to `onLeave.invoke(code, reason)`. The server declines the
 * reconnection window for this code as well (`descent.room.ts`'s `onDrop`), so
 * the two sides agree: the seat is gone and nothing is coming back for it.
 *
 * ## …and what it cost arrives just before it, as a message
 *
 * A close code is one integer and a toll is two of them, so the bill travels on
 * a channel of its own: `SERVER_MESSAGE.runToll`, addressed to the seat that
 * paid it and **sent on the still-open socket immediately before the close**
 * (`descent.room.ts`'s `endRun`, `@d2/schema`'s `RunTollPayload`). That ordering
 * is the whole reason this file can quote a figure at all, and it holds because
 * the room has a test for it rather than because the two happen to race in the
 * right direction — so the client latches the message when it lands and reads it
 * when the close fires (`./game-canvas`).
 */

/** What `apps/game`'s `RUN_ENDED_CODE` is. See the header for why it is not 4000. */
export const RUN_ENDED_CODE = 4200;

/**
 * The word the room sends beside it, for a log rather than for a decision.
 *
 * Deliberately **not** part of `endedRun`'s test. A close reason is free text
 * on a frame that gives it 123 bytes, proxies are entitled to drop it, and a
 * client that demanded the string would answer a truncated one with *"the
 * server could not be reached"* — the failure this module exists to end,
 * reintroduced by a stricter check.
 */
export const RUN_ENDED_REASON = 'run-ended';

/** Whether a socket that closed did so because the run is over. */
export const endedRun = (code: number): boolean => code === RUN_ENDED_CODE;

/**
 * What a death took, as the room charged it.
 *
 * The client's own shape for `@d2/schema`'s `RunTollPayload`, declared here
 * rather than imported for the reason `character-feed.ts` declares
 * `ProgressionLedger` and `prediction.ts` declares its own inputs (`TODO.md`
 * #185): `./wire` is the one module in this package that reads the wire, so
 * everything downstream of it — this file, the panels, the screens — stays
 * testable without `@colyseus/schema` and without a socket.
 *
 * Both numbers are **outcomes and not rules**. `gold` is what was actually taken
 * out of the purse, never the percentage it came from (`@d2/core`'s
 * `deathGoldForfeit` owns that and neither side restates it), and `items` is a
 * **count** of the things that went with it rather than a list of them — the
 * ids the room holds identify rows in one character's grid, so they would name
 * nothing this client could draw (`@d2/schema`'s `RunTollPayload` argues it at
 * length, and a corpse-run mode is what would change the answer).
 *
 * What is **not** here is the purse that survived, and its absence is load
 * bearing. The toll says what was taken; what remains is the roster's, from the
 * API that stores it. Nothing in this package may compute one from the other:
 * the row a tab holds is *pre-run*, the run had been adding to the purse ever
 * since, and the forfeit is a proportion of the purse as it stood at the moment
 * of death — so a subtraction anywhere on this side produces a balance no
 * player owns.
 */
export interface RunToll {
  /** Gold the death actually took out of the purse. Whole gold, already charged. */
  readonly gold: number;
  /** How many things went out of the packing grid with it. A count, never a list. */
  readonly items: number;
}

/**
 * A death that cost nothing at all.
 *
 * A real toll and not the absence of one: a character that walked in
 * empty-handed and picked nothing up loses nothing, a quarter of a small purse
 * floors to zero, and the room sends the message either way. There is therefore
 * no *"not told yet"* state anywhere on this side — which is what lets every
 * signature below take a `RunToll` rather than a `RunToll | null`.
 */
export const NOTHING_LOST: RunToll = Object.freeze({ gold: 0, items: 0 });

/**
 * Grouped in `en-US` explicitly, and a copy of `ui/town-screen-model.ts`'s
 * `countLabel` rather than an import of it.
 *
 * The dependency would run backwards. `ui/` reads `game/` — `town-screen-model`
 * imports this file for the words a death is announced in — so importing the
 * formatter back would close a cycle between the two layers to save three
 * lines. That file makes the same trade against `hud-model.ts` and states why:
 * a duplicated *rule* goes stale because there is an authority for it to drift
 * from, and grouping digits is not a rule.
 *
 * The guard is the same one, for a sharper reason here. Everything else this
 * client reads off the wire is a decoded `@colyseus/schema` field; a message is
 * msgpack, so its shape is a promise the sender made rather than something the
 * decoder enforced. This is where that promise stops being able to put `NaN` in
 * front of a player.
 */
const GROUPED = new Intl.NumberFormat('en-US');

const countable = (value: number): number => (Number.isFinite(value) ? Math.max(0, Math.round(value)) : 0);

/** What was taken, in words — or `null` for a half of the bill that took nothing. */
const goldTaken = (gold: number): string | null =>
  countable(gold) === 0 ? null : `${GROUPED.format(countable(gold))} gold`;

const itemsTaken = (items: number): string | null =>
  countable(items) === 0 ? null : `${GROUPED.format(countable(items))} ${countable(items) === 1 ? 'thing' : 'things'} you picked up down there`;

/**
 * The bill in words: both halves, one of them, or the news that there was none.
 *
 * Each half is dropped when it is zero rather than printed as `0 gold`, because
 * a zero on a bill reads as a line item and both of these are ordinary: a
 * quarter of three gold floors to nothing, and a bag can be empty. When both
 * are, the sentence says so outright — *"nothing"* is the true and complete
 * answer, and it is the one thing a player who lost a run and nothing else
 * wants to read.
 */
const describeToll = (toll: RunToll): string => {
  const halves = [goldTaken(toll.gold), itemsTaken(toll.items)].filter((half): half is string => half !== null);

  return halves.length === 0 ? 'nothing' : halves.join(' and ');
};

/**
 * What a player reads when they get to town.
 *
 * **It quotes the toll now**, which is the whole of `TODO.md` #486 arriving on
 * this side. Until `SERVER_MESSAGE.runToll` existed this said *"some of the gold
 * you were carrying"* — the server charged the death, threw the figures away and
 * left the player to audit the game — and the sentence could not have done
 * better, because a close code is one integer and no message carried the rest.
 *
 * Three things it says, in the order a player cares about them. **That they
 * died and the run is over**, which is the fact the close code already carried.
 * **What it cost**, from the message and from nothing else. And **what it did
 * not cost**: the toll takes the packing grid and the purse and can take nothing
 * else, because nothing else is handed to it (`docs/GAME-DESIGN.md`, `TODO.md`
 * #3) — so a player who has just lost gear they found needs to know their kit is
 * still on and their stash is still there.
 *
 * Two things it must never do. It must not **promise a respawn**: the corpse
 * used to stand up at the entrance three seconds later, it does not any more,
 * and a sentence that said so would describe a build nobody is running. And it
 * must not **name a surviving purse**. The only figures in it are the two the
 * room charged; what is left is the roster's answer, drawn in the bar above this
 * sentence by a caller that re-read it (`account/AccountGate.tsx`). A subtraction
 * here would be a balance invented from a pre-run row.
 */
export const runEndedText = (toll: RunToll): string =>
  `You died. The run is over, and it cost you ${describeToll(toll)}. What you had equipped and what you left in town is safe.`;
